/* =========================
    IMPORTS & CORE PACKAGES
   ========================= */
import React, { useState } from "react";
import { auth, db } from "../firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc, collection, getDocs, query, where } from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Phone, UserCheck, Lock, Eye, EyeOff } from "lucide-react";

export default function Register() {
  const navigate = useNavigate();

  /* ===============================
      COMPONENT DEFINITION & STATES
     =============================== */

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("student");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  /* ==========================
      SYSTEM IDENTIFIERS SETUP
     ========================== */

  async function generateCustomID(userRole) {
    const q = query(collection(db, "users"), where("role", "==", userRole));
    const snapshot = await getDocs(q);
    const count = snapshot.size + 1;
    const paddedNum = String(count).padStart(3, "0");
    const prefix = userRole === "student" ? "@STD" : "@INS";
    return `${prefix}${paddedNum}`;
  }

  /* ==============================
      REGISTRATION ACTION HANDLERS
     ============================== */

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      setError("");
      setLoading(true);
  
     if (password !== confirmPassword) {
      setError("⚠️ Confirmation Error: Passwords do not match!");
      setLoading(false);
      return;
    }

      // Password Validation Guard: Ensures password matches minimum, maximum, capital letter, and symbol rules
      if (password.length < 6 || password.length > 15) {
        setError("⚠️ Password must be between 6 and 15 characters long.");
        setLoading(false);
        return;
      }

      const hasCapitalLetter = /[A-Z]/.test(password);
      const hasSpecialSymbol = /[\W_]/.test(password);

      if (!hasCapitalLetter) {
        setError("⚠️ Password must contain at least one capital letter.");
        setLoading(false);
        return;
      }

      if (!hasSpecialSymbol) {
        setError("⚠️ Password must contain at least one special symbol (e.g. @, #, $, %).");
        setLoading(false);
        return;
      }

      // Create the authentication profile first (Proceeds only if password validations pass)
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const customId = await generateCustomID(role);

      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        fullName,
        email,
        phone,
        role,
        status: "pending",
        customId,
        createdAt: new Date().toISOString()
      });

      await setDoc(doc(db, "auditLogs", `reg-${user.uid}`), {
        action: "User Registration",
        userId: user.uid,
        details: `Profile registered. Assigned ID: ${customId}`,
        timestamp: new Date().toISOString()
      });

      alert("Registration completed! Please await administrative approval.");
      navigate("/login");
    } catch (err) {
      setError("Failed to create profile: " + err.message);
    }
    setLoading(false);
  }

  /* ===========================
      SIGN-UP VIEWPORT RENDERER
     =========================== */
  return (
    <div 
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "var(--bg-slate)",
        padding: "1.5rem"
      }}
    >
      <div 
        className="card" 
        style={{ 
          maxWidth: "500px", 
          width: "100%", 
          margin: "0", 
          padding: "2.5rem 2rem",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
        }}
      >
        {/* Card Header Brand */}
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div 
            style={{ 
              display: "inline-flex", 
              background: "#b7cbf7", 
              padding: "1rem", 
              borderRadius: "50%",
              marginBottom: "1rem"
            }}
          >
            <img src="/logo192.png" alt="Logo" style={{ width: "70px", height: "auto" }} />
          </div>
          <h2 style={{ color: "var(--navy-primary)", fontSize: "1.75rem", fontWeight: "800", marginBottom: "0.5rem" }}>
            Create Account
          </h2>
          <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>
            Provide details below to request access credentials.
          </p>
        </div>

        {error && (
          <div 
            style={{ 
              color: "var(--danger)", 
              backgroundColor: "#fee2e2", 
              padding: "0.75rem 1rem", 
              borderRadius: "8px", 
              fontSize: "0.85rem",
              fontWeight: "600",
              marginBottom: "1.5rem",
              textAlign: "center"
            }}
          >
            {error}
          </div>
        )}

        {/* Registration Inputs Form */}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><UserCheck size={16} /> Full Name</label>
            <input type="text" className="form-control" required value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>

          <div className="form-group">
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><Mail size={16} /> Email Address</label>
            <input type="email" className="form-control" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          <div className="form-group">
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><Phone size={16} /> Phone Number</label>
            <input type="text" className="form-control" required maxLength={10} value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} />
          </div>

          <div className="form-group">
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><UserCheck size={16} /> Select Role</label>
            <select className="form-control" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="student">Student</option>
              <option value="instructor">Instructor</option>
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: "2rem" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><Lock size={16} /> Enter Password</label>
            <div style={{ position: "relative" }}>
              <input 
                type={showPassword ? "text" : "password"} 
                className="form-control" 
                required 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                style={{ paddingRight: "2.5rem" }}
              />
              <button 
                type="button" 
                onClick={() => setShowPassword(!showPassword)}
                style={{ position: "absolute", right: "0.75rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", display: "flex", alignItems: "center" }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Confirm Password Input Field */}
          <div className="form-group" style={{ marginBottom: "2rem" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><Lock size={16} /> Confirm Password</label>
            <div style={{ position: "relative" }}>
              <input 
                type={showPassword ? "text" : "password"} 
                className="form-control" 
                required 
                value={confirmPassword} 
                onChange={(e) => setConfirmPassword(e.target.value)} 
              />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: "absolute", right: "0.75rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", display: "flex", alignItems: "center" }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
            </div>
          </div>

          <button 
            type="submit" 
            className="navy-btn" 
            style={{ width: "100%", justifyContent: "center", padding: "1rem" }} 
            disabled={loading}
          >
            Request Access Approval
          </button>
        </form>

        <p style={{ textAlign: "center", marginTop: "1.5rem", color: "var(--text-muted)", fontSize: "0.9" }}>
          Already registered? <Link to="/login" style={{ color: "var(--navy-light)", fontWeight: "600", textDecoration: "none" }}>Log In</Link>
        </p>
      </div>
    </div>
  );
}